import { defineComponent, inject, onMounted, onUnmounted, ref } from 'vue'
import { useAppStore } from '@/pages/chargermap/stores/app'
import { Vue3Lottie } from 'vue3-lottie'
import styles from './styles.module.scss'
import CityMapView from './map/CityMapView'
import { BannerItem } from '../models/config.model'
import { getBannerList, getUnreachedBannerList } from '../apis/map.api'
import { Swipe, SwipeItem } from 'vant'
import type { SwipeInstance } from 'vant'
import VideoView from '../components/video/index'
import { OS } from '@/utils/device'
import AutoDisplay from '../components/autoDisplay'
import CityMapViewXinFa from './map/CityMapViewXinFa'
import { useComponentStore } from './localStores/component'
import Tracker from '@nio-fe/nio-tracker-web'
import MapIndex from './map'
import RouterView from '../router'

export default defineComponent({
  name: 'NSH5',
  props: [],
  setup: () => {
    const appStore = useAppStore()
    const componentStore = useComponentStore()
    const channel = inject<string>('channel', '')
    const client = inject<string>('client', '')
    const accessId = inject<string>('uuid', '')
    const isPopupScreen = ref<boolean>(false)
    const screenPopupSource = ref<string>()
    const screenPopupSourcePreList = ref<BannerItem[]>([])
    const isLottie = ref<boolean>(false)
    const brand = (process.config as any).brand // alps 目前暂时不需要运营位弹窗

    let mapDurationTimer: any = null // 地图停留时长定时器

    // 获取组件配置
    const componentsConfig: any = window.NSComponentsConfig

    if (!componentsConfig.implant?.show) {
      // 不允许被嵌入的情况下不读取参数
      window.iframeParams = null
    }

    const swipeRef = ref<SwipeInstance>()

    // 上报停留时长
    const pushStayDuration = () => {
      Tracker.track(
        'nsh5_stayduration',
        Object.assign(appStore.trackParamsCommon, { accessId, timestamp: new Date().getTime() })
      )
    }

    // 倒计时关闭全屏弹屏
    const closeScreenPopup = () => {
      setTimeout(() => {
        isPopupScreen.value = false
      }, componentsConfig?.index_popup?.properties?.close_time || 5000)
    }

    // 弹屏并缓存
    const screenPopupCache = (id: string, item: BannerItem) => {
      // 取弹屏本地缓存
      const screenPopupList = localStorage.getItem('screenPopupList_pc')
      // 弹屏本地缓存没有的触发弹屏
      if (!screenPopupList || !screenPopupList.includes(id, 0)) {
        // 弹屏物料匹配赋值
        item &&
          window
            .fetch(item?.picture)
            .then((response) => response.json())
            .then((json) => {
              screenPopupSource.value = json
            })
        // 是否Lotti动画
        if (item?.picture?.split('.').pop()?.includes('json', 0)) {
          isLottie.value = true
        }
        // 显示全屏弹屏
        isPopupScreen.value = true
        // 触发倒计时关闭
        closeScreenPopup()
        // 记录弹屏本地缓存
        !screenPopupList
          ? localStorage.setItem('screenPopupList_pc', id)
          : localStorage.setItem('screenPopupList_pc', `${screenPopupList},${id}`)
      }
    }

    // 全屏弹屏，已开始正在进行
    const getScreenPopup = () => {
      getBannerList('nsh5_pc_homepage_reminder').then((res: any) => {
        if (res.isValid() && res.data.length > 0) {
          const item = res.data[0]
          // 弹屏并缓存
          item && screenPopupCache(item.id, item)
        }
      })
    }

    // 全屏弹屏预请求，未开始
    const getScreenPopupPre = () => {
      getUnreachedBannerList('nsh5_pc_homepage_reminder').then((res: any) => {
        if (res.isValid() && res.data.length > 0) {
          screenPopupSourcePreList.value = res.data
          res.data.map((item: BannerItem, index: number) => {
            const img = new Image()
            img.addEventListener('load', () => {
              // 图片加载完成
            })
            img.src = res.data[index]?.picture
            img.className = styles.hide
            document.body.appendChild(img)
          })
        }
      })
    }

    // 弹屏物料匹配&&弹屏显示
    componentStore.bus.on('screenPopupShow', (id: string) => {
      // id匹配
      const matchItem = screenPopupSourcePreList.value.find((c: BannerItem) => c.id === id)
      // 弹屏并缓存
      matchItem && screenPopupCache(id, matchItem)
    })

    componentStore.bus.on('onPushStayDuration', () => {
      pushStayDuration()
    })

    onMounted(() => {
      pushStayDuration()
      if (componentsConfig?.index_popup?.show) {
        getScreenPopup()
        getScreenPopupPre()
        setTimeout(() => {
          getScreenPopupPre()
        }, componentsConfig?.index_popup?.properties?.retry_time || 15 * 60 * 1000)
      }
      componentsConfig?.swipe_component?.show && initMapDuration() // 读取配置，启动首页倒计时轮播
    })

    onUnmounted(() => {
      componentStore.bus.off('screenPopupShow')
      componentStore.bus.off('onPushStayDuration')
    })

    window.onunload = function () {
      // 将退出时间存于localstorage中
      localStorage.setItem('leaveTime', String(new Date().getTime()))
      pushStayDuration()
      mapDurationTimer && clearTimeout(mapDurationTimer)
    }

    const changeSwipe = (index: number) => {
      index === 0 && initMapDuration() // 回到首页启动首页倒计时
      componentStore.$patch({ swipeIndex: index })
    }

    // 初始化地图停留时长
    const initMapDuration = () => {
      if (componentsConfig?.auto_zoom?.show) return // 若配置了auto_zoom，则map_duration会失效
      mapDurationTimer && clearTimeout(mapDurationTimer)
      mapDurationTimer = setTimeout(
        () => {
          swipeRef.value?.next()
        },
        componentsConfig?.swipe_component?.show &&
          componentsConfig?.swipe_component?.properties?.map_duration > 0
          ? componentsConfig?.swipe_component?.properties?.map_duration
          : 60000
      )
    }

    const handlePlayEnded = () => {
      swipeRef.value?.next()
    }

    const swipeMap = () => {
      swipeRef.value?.next()
    }

    const mapIndex = () => {
      window.backHistoryNum = 0
      const baseUI = () => {
        return (
          <>
            {client !== 'mobile' && <RouterView></RouterView>}
            {isPopupScreen.value && screenPopupSource.value && (brand === 'nio' || brand === 'alps') ? (
              <div class={styles.popupScreen}>
                {isLottie.value ? (
                  <Vue3Lottie loop={false} class="lottie" animationData={screenPopupSource.value} />
                ) : null}
              </div>
            ) : null}
          </>
        )
      }
      if (
        (channel == 'store' || channel.indexOf('xinfa') > -1) &&
        client == 'android' &&
        componentsConfig?.xinfa?.show
      ) {
        // 信发设备，安卓
        return (
          <>
            <CityMapViewXinFa></CityMapViewXinFa>
            {baseUI()}
          </>
        )
      }
      return (
        <>
          {/* <CityMapView
            xinfaView={(channel == 'store' || channel.indexOf('xinfa') > -1) && client == 'android'}
          ></CityMapView> */}
          <MapIndex></MapIndex>
          {baseUI()}
        </>
      )
    }

    return () => (
      <div class={styles.page}>
        {componentsConfig?.swipe_component?.show &&
        componentsConfig?.swipe_component?.properties?.sources &&
        componentsConfig?.swipe_component?.properties?.sources.length > 0 ? (
          <Swipe
            ref={swipeRef}
            class={styles.swipe}
            show-indicators={false}
            touchable={false}
            onChange={changeSwipe}
          >
            <SwipeItem>
              <div
                class={styles.displayBox}
                style={{ visibility: componentStore.swipeIndex === 0 ? 'visible' : 'hidden' }}
              >
                {mapIndex()}
              </div>
            </SwipeItem>
            <SwipeItem>
              <div
                class={styles.displayBox}
                style={{ display: componentStore.swipeIndex != 0 ? 'visible' : 'hidden' }}
              >
                <VideoView
                  sources={componentsConfig.swipe_component.properties.sources}
                  muted={!componentsConfig.swipe_component.properties.unmuted}
                  onPlayEnded={handlePlayEnded}
                ></VideoView>
              </div>
            </SwipeItem>
          </Swipe>
        ) : (
          mapIndex()
        )}
        {componentsConfig?.auto_zoom?.show && <AutoDisplay onSwipe={swipeMap}></AutoDisplay>}
      </div>
    )
  },
})
